import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { CheckCircle2, Loader2, Users } from 'lucide-react';
import { waitlistApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TeamsWaitlistDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [company, setCompany] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError('Email is required');
      return;
    }
    try {
      setLoading(true);
      await waitlistApi.joinTeams({
        email,
        company: company || null,
        team_size: teamSize || null,
        notes: notes || null,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Could not join waitlist');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    // Reset after close animation
    setTimeout(() => {
      setSuccess(false);
      setError(null);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1A1A1A] border-gray-800 text-white"
        data-testid="teams-waitlist-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Users className="w-5 h-5 text-[#9D00FF]" />
            Teams — Join the Waitlist
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            CyberInsti for Teams is launching soon. Tell us about your org and we'll reach out with early
            access.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-10 text-center space-y-3" data-testid="waitlist-success">
            <CheckCircle2 className="w-12 h-12 text-[#00FF00] mx-auto" />
            <p className="text-white text-lg font-semibold">You're on the list.</p>
            <p className="text-gray-400 text-sm">
              We'll email <span className="text-white">{email}</span> when Teams is ready.
            </p>
            <Button
              onClick={close}
              className="bg-[#00FF00] text-black hover:bg-[#00DD00]"
              data-testid="waitlist-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-gray-300">Work email *</Label>
              <Input
                data-testid="waitlist-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-[#0A0A0A] border-gray-800 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Company</Label>
              <Input
                data-testid="waitlist-company-input"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Inc."
                className="bg-[#0A0A0A] border-gray-800 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">Team size</Label>
              <Input
                data-testid="waitlist-team-size-input"
                value={teamSize}
                onChange={(e) => setTeamSize(e.target.value)}
                placeholder="e.g. 10-50"
                className="bg-[#0A0A0A] border-gray-800 text-white"
              />
            </div>
            <div>
              <Label className="text-gray-300">What brings you here?</Label>
              <Textarea
                data-testid="waitlist-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Compliance training, SOC onboarding, red team prep..."
                className="bg-[#0A0A0A] border-gray-800 text-white"
              />
            </div>
            {error && (
              <p className="text-sm text-red-400" data-testid="waitlist-error">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit-btn"
                className="bg-[#9D00FF] text-white hover:bg-[#8800DD]"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join Waitlist'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeamsWaitlistDialog;
